/**
 * RiskStatusChip — shared MUI Chip for a `RiskSnapshot.status` value.
 *
 * Centralizes the machine-readable → friendly label + color mapping so every
 * page in the Risk Management module (Org Dashboard, Top Risks, Project
 * Summary, Risk Trend, etc.) renders the same chip for the same status.
 *
 * Backend status values live on `RiskSnapshot.STATUS_CHOICES`:
 *   open / in_progress / mitigated / closed / reappeared
 */

import React from "react";
import { Chip } from "@mui/material";

type ChipColor =
  | "default"
  | "primary"
  | "info"
  | "success"
  | "warning"
  | "error";

const STATUS_META: Record<string, { label: string; color: ChipColor }> = {
  open: { label: "Open", color: "info" },
  in_progress: { label: "In progress", color: "warning" },
  mitigated: { label: "Mitigated", color: "success" },
  closed: { label: "Closed", color: "default" },
  reappeared: { label: "Reappeared", color: "error" },
};

interface RiskStatusChipProps {
  status: string | null | undefined;
  size?: "small" | "medium";
}

const RiskStatusChip: React.FC<RiskStatusChipProps> = ({
  status,
  size = "small",
}) => {
  const raw = (status ?? "").toString();
  const meta =
    STATUS_META[raw] ?? {
      label: raw ? raw.replace(/_/g, " ") : "—",
      color: "default" as ChipColor,
    };
  return (
    <Chip
      label={meta.label}
      color={meta.color}
      size={size}
      variant="outlined"
      sx={{ textTransform: "capitalize", fontWeight: 500 }}
    />
  );
};

export default RiskStatusChip;
